/**
 * This adds hooks for keyboard handling to DFGrid.
 * 
 * The methods for each keycombo should be over-ridden by child classes when needed.
 * 
 * The fired events should NOT be observed. They are intended to simplify unit testing.
 */

var doKendoDefaultHook = function(name) {
    return function(data) {
        this.trigger("_keyboardHook", {
            key: name,
            data: data
        });
        return false;
    }
};
export default {


    /**
     * Cleanup keydown handler for grid.
     * @private
     */
    _cleanupKeyboardHook: function() {
        if (this.element && this.element.length && this._keyboardHook) {
            this.element[0].removeEventListener("keydown", this._keyboardHook, true);
            delete this._keyboardHook;
        }
    },

    /**
     * Setup keydown handler for grid.
     * @private
     */
    _prepareKeyboardHooks: function() {
        this._cleanupKeyboardHook();
        this._keyboardHook = (e) => {
            const current = this.current();
            if (current && current.length) {
                const data = {
                    cell: current
                };
                data.isInFooter = !($(e.target).closest(".k-grid-pager, .k-grid-footer, .k-grid").hasClass("k-grid"));
                data.isEditCell = data.cell.find("a.k-grid-edit-command").length > 0;
                data.isDragCell = data.cell.find("a.k-grid-drag").length > 0;
                data.row = data.cell.closest("tr");
                data.isHeaderRow = this.thead && this.thead.length && $.contains(this.thead[0], data.row[0]);
                data.isGroupRow = data.row.hasClass("k-grouping-row");
                data.isEditRow = data.row.hasClass("k-grid-edit-row");
                data.isDetailRow = data.row.hasClass("k-detail-row");
                data.hasDetailRow = data.row.hasClass("k-master-row");
                data.isStandardRow = !data.isGroupRow && !data.isHeaderRow && !data.isDetailRow;
                data.key = e.keyCode;
                data.altKey = e.altKey;
                data.ctrlKey = e.ctrlKey || e.metaKey;
                data.shiftKey = e.shiftKey;
                data.alone = !data.altKey && !data.ctrlKey && !data.shiftKey;
                data.target = e.target;
                const handled = this._dispatchKeyDown(data);
                if (handled) {
                    e.preventDefault();
                    e.stopPropagation();
                }
            }
        };
        this.element.each((i, el) => el.addEventListener("keydown", this._keyboardHook, true));
    },

    _dispatchKeyDown: function(data) {
        if (!data.isInFooter && !data.isHeaderRow && !data.isEditRow) {
            if (data.key == kendo.keys.SPACEBAR) {
                if (data.alone) {
                    return this.spaceKey(data);
                } else if (!data.shiftKey && data.ctrlKey && !data.altKey) {
                    return this.ctrlSpaceKey(data);
                } else if (data.shiftKey && !data.ctrlKey && !data.altKey) {
                    return this.shiftSpaceKey(data);
                }
            } else if (data.key == kendo.keys.ENTER && data.alone) {
                return this.enterKey(data)
            } else if (data.key == kendo.keys.DOWN && data.altKey && !data.ctrlKey && !data.shiftKey) {
                return this.altDownKey(data);
            } else if (data.key == kendo.keys.UP && data.altKey && !data.ctrlKey && !data.shiftKey) {
                return this.altUpKey(data);
            } else if (!data.ctrlKey && (data.key == kendo.keys.UP || data.key == kendo.keys.DOWN || data.key == kendo.keys.LEFT || data.key == kendo.keys.RIGHT)) {
                return this._catchNextNavigate();
            } else if (data.key == kendo.keys.ESC && data.alone) {
                return this.escKey(data);
            }
        } else if (data.isHeaderRow && data.key == kendo.keys.SPACEBAR && data.alone) {
            return this._handleHeaderCheckbox(data);
        }
        return false;
    },

    /**
     * Handle a header checkbox.
     * @param data
     * @returns {boolean}
     * @private
     */
    _handleHeaderCheckbox: function(data) {
        var checkbox = data.cell.find("input.checkboxCommandHeader");
        if (checkbox.length == 1) {
            checkbox.click();
            return true;
        }
        return false;
    },

    /**
     * Deal with incoming navigation event.
     * @private
     */
    _catchNextNavigate: function() {
        if (this.options.selectOnFocus && this.options.navigatable && this.options.selectable) {
            var handle = function() {
                var now = this.current();
                var row = now.closest("tr[data-uid]:not(.k-grouping-row):not(.k-detail-row)");
                if (row.length) {
                    this.select(row);
                }
            };
            this.one("navigate", handle);
            var me = this;
            setTimeout(function() {
                me.unbind("navigate", handle);
            }, 0);
        }
        return false;
    },

    _triggerActionInElement: function(el) {
        el.find("a,input,select,button")
            .filter(":not(.checkboxCommand)")
            .filter(":not(.checkboxGroup)")
            .filter(":not(.k-icon)")
            .filter(":last")
            .each(function(i, el) {
                el.click();
            });
    },

    /**
     * Fire the action element in the group header.
     * @param data
     */
    triggerGroupAction: function(data) {
        if (data.isGroupRow) {
            this._triggerActionInElement(data.row);
        }
    },

    /**
     * Trigger any actions in the current cell.
     * @param data
     */
    triggerCellAction: function(data) {
        if (!data.isGroupRow && !data.isEditCell) {
            this._triggerActionInElement(data.cell);
        }
    },

    /**
     * Fire the edit action if current cell is the editable cell.
     * @param data
     */
    triggerEditAction: function(data) {
        if (data.isEditCell) {
            this.editRow(data.row);
        }
    },

    /**
     * Expand/Collapse the detail row if one is relevant.
     * @param data
     */
    triggerDetailRow: function(data) {
        if (data.hasDetailRow) {
            this.toggleDetailRow(data.row);
        }
    },

    /**
     * Trigger double click if needed on the row.
     * @param data
     */
    triggerDoubleClick: function(data) {
        if (this.options.doubleClickOnEnterKey) {
            this.doubleClick(data.row);
        }
    },

    /**
     * Trigger row selection.
     * @param data
     */
    triggerRowSelection: function(data) {
        if (data.isStandardRow && this.options.selectable) {
            this.clearSelection();
            this.select(data.row);
        }
    },

    /**
     * Trigger group toggle if row is a group row.
     * @param data
     */
    triggerGroupToggle: function(data) {
        if (data.isGroupRow) {
            data.row.find(".k-svg-i-caret-alt-down, .k-svg-i-caret-alt-right").click();
        }
    },

    /**
     * Trigger a group checkbox.
     * @param data
     */
    triggerGroupCheck: function(data) {
        if (data.isGroupRow) {
            data.row.find(".checkboxGroup").click();
        }
    },

    /**
     * Trigger row re-order in a direction
     * @param data
     * @param adjust 1 or -1
     */
    triggerKeyboardReorder: function(data, adjust) {
        if (data.isDragCell) {
            this.adjustRowPosition(data.row, adjust);
        }
    },

    /**
     * Handle the space key. Extension point.
     */
    spaceKey: doKendoDefaultHook("spaceKey"),

    /**
     * Handle the [ctrl+space] key. Extension point.
     */
    ctrlSpaceKey: doKendoDefaultHook("ctrlSpaceKey"),

    /**
     * Handle the [shift+space] key. Extension point.
     */
    shiftSpaceKey: doKendoDefaultHook("shiftSpaceKey"),

    /**
     * Handle the [alt+down] key. Extension point.
     */
    altDownKey: doKendoDefaultHook("altDownKey"),

    /**
     * Handle the [alt+up] key. Extension point.
     */
    altUpKey: doKendoDefaultHook("altUpKey"),

    /**
     * Handle the [enter] key. Extension point.
     */
    enterKey: doKendoDefaultHook("enterKey"),

    /**
     * Handle the [esc] key. Extension point.
     */
    escKey: doKendoDefaultHook("escKey")
};
